import { bffAuthService } from '../services/BffAuthService';

interface AuthRequestInit extends RequestInit {
  headers?: Record<string, string>;
}

interface AuthFetchOptions {
  skipAuth?: boolean;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
}

export async function authFetch(url: string, options: AuthRequestInit = {}, authOptions: AuthFetchOptions = {}): Promise<Response> {
  const {
    skipAuth = false,
    timeout = 30000,
    maxRetries = 1,
    retryDelay = 1000
  } = authOptions;

  let retryCount = 0;
  let lastError: Error | null = null;

  while (retryCount <= maxRetries) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    const requestOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include'
    };

    if (options.body instanceof FormData) {
      delete (requestOptions.headers as any)['Content-Type'];
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 401 && !skipAuth && retryCount < maxRetries) {
        console.warn(`authFetch - 401 error, rechecking session (${retryCount + 1}/${maxRetries}):`, url);

        const stillAuthenticated = await bffAuthService.checkSessionStatus();
        if (!stillAuthenticated) {
          bffAuthService.login();
          return response;
        }

        retryCount++;
        lastError = new Error('HTTP 401 Unauthorized');
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        lastError = new Error(`Request timeout (${timeout}ms)`);
      } else {
        lastError = error as Error;
      }

      if (retryCount < maxRetries) {
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      throw lastError;
    }
  }

  throw lastError || new Error('Request failed');
}

export async function authGet(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'GET' }, authOptions);
}

export async function authPost(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'POST' }, authOptions);
}

export async function authPut(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'PUT' }, authOptions);
}

export async function authDelete(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'DELETE' }, authOptions);
}